import React, { useState, useEffect } from "react";
import { Row, Col, Card, Button, Alert, Spinner } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import {
  createMeteredBillingCheckoutSession,
  checkSubscriptionStatus,
} from "../../../Services/StripeService";
import { getUserDetailsAPI } from "../../../Utilities/APIHelper";
import { get } from "../../../Services/AxiosService";
import SpinnerLoader from "../../../Components/SpinnerLoader";
import { getAllSupportTeamMembers } from "../../../Services/LeaderSupportTeamMember";

// Define subscription plan with pay-as-you-go model
const SUBSCRIPTION_PLAN = {
  id: "price_1RVjU0PrutU1IoZlwJW4XUzc",
  name: "Group Subscription",
  description:
    "First 100 support team members are free, then pay for all members",
  basePrice: "$0.00",
  perUnitPrice: "$0.50",
  interval: "month",
  features: [
    "No minimum commitment",
    "Automatic billing based on actual usage",
    "Full access to all Group Leader features",
    "Usage reporting and analytics",
  ],
};

export default function SubscriptionSignup() {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [currentTeamMemberCount, setCurrentTeamMemberCount] = useState(0);
  const [teamMemberCountLoading, setTeamMemberCountLoading] = useState(true);
  const history = useHistory();

  useEffect(() => {
    async function fetchData() {
      try {
        // Get user data
        const response = await get(getUserDetailsAPI);
        const user = response.data.response;
        setUserData(user);

        // Check with backend if user has active subscription
        if (user.email) {
          const subCheck = await checkSubscriptionStatus(user.email);
          if (subCheck.hasActiveSubscription) {
            setHasSubscription(true);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(
          "Failed to load user data or check subscription status. Please try again."
        );
        setLoading(false);
      }
    }
    fetchData();
  }, [history]);

  // Get actual support team member count
  useEffect(() => {
    async function getTeamMemberCount() {
      try {
        setTeamMemberCountLoading(true);
        const res = await getAllSupportTeamMembers();
        const result = JSON.parse(res.response);

        // Use the activeSupportTeamCount from the API response
        if (result && result.activeSupportTeamCount !== undefined) {
          setCurrentTeamMemberCount(result.activeSupportTeamCount);
        }
        setTeamMemberCountLoading(false);
      } catch (err) {
        console.error("Error getting support team count:", err);
        setTeamMemberCountLoading(false);
      }
    }

    getTeamMemberCount();
  }, []);

  const handleSubscribe = async () => {
    if (!userData || !userData.id || !userData.email) {
      setError("User information not available. Please try again.");
      return;
    }

    setProcessing(true);

    try {
      console.log("Starting checkout process with:", {
        planId: SUBSCRIPTION_PLAN.id,
        userId: userData.id,
        email: userData.email,
      });

      // Use the metered billing specific function
      const result = await createMeteredBillingCheckoutSession(
        SUBSCRIPTION_PLAN.id,
        userData.id,
        userData.email
      );

      console.log("Checkout session result:", result);

      if (!result.success) {
        throw new Error(result.error || "Failed to create checkout session");
      }
      // Redirect happens inside createMeteredBillingCheckoutSession
    } catch (err) {
      console.error("Error starting checkout:", err);
      setError(err.message || "An unexpected error occurred");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="container p-0 text-center mt-5">
        <SpinnerLoader />
        <p className="mt-3">Loading subscription information...</p>
      </div>
    );
  }

  if (hasSubscription) {
    return (
      <div className="container p-0">
        <div className="text-center mt-5">
          <Alert variant="success">
            <h4>You already have an active subscription!</h4>
            <p>Redirecting to dashboard...</p>
          </Alert>
          <Button
            variant="primary"
            className="mt-3"
            onClick={() => history.push("/groupleader/groupleaderdashboard")}
          >
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Calculate potential costs based on updated billing model
  const freeAllowance = 100;
  const exceedsFreeAllowance = currentTeamMemberCount > freeAllowance;
  // If over 100, charge for ALL members, not just the ones over the allowance
  const billableMembers = exceedsFreeAllowance ? currentTeamMemberCount : 0;
  const estimatedMonthlyCost = billableMembers * 0.5; // $0.50 per member

  return (
    <React.Fragment>
      <div className="container p-0">
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        <div className="dashboard-wrapper">
          <Row className="justify-content-center">
            <Col md={10} lg={8}>
              <Card
                className="mb-4 shadow border-0"
                style={{ borderRadius: "1rem" }}
              >
                <Card.Header
                  as="h4"
                  className="text-white py-4 text-center"
                  style={{
                    background:
                      "linear-gradient(135deg, #2660a5 0%, #4a8eda 100%)",
                    borderTopLeftRadius: "1rem",
                    borderTopRightRadius: "1rem",
                  }}
                >
                  {SUBSCRIPTION_PLAN.name}
                </Card.Header>
                <Card.Body className="px-md-5 py-5">
                  <div className="text-center mb-5">
                    <h2 className="pricing-header mb-4">
                      <span className="display-5 font-weight-bold">
                        {SUBSCRIPTION_PLAN.basePrice}
                      </span>
                      <span className="text-muted"> base fee</span>
                    </h2>
                    <div
                      className="pricing-details p-4 mx-auto"
                      style={{
                        maxWidth: "500px",
                        background: "#f8f9fa",
                        borderRadius: "0.75rem",
                      }}
                    >
                      <p className="lead mb-2">
                        <i className="fa fa-check-circle text-success mr-2"></i>
                        Free for up to 100 support team members
                      </p>
                      <p className="lead mb-0">
                        <i className="fa fa-info-circle text-primary mr-2"></i>
                        <span className="font-weight-bold">
                          {SUBSCRIPTION_PLAN.perUnitPrice} per member
                        </span>{" "}
                        once you exceed 100
                      </p>
                    </div>
                  </div>

                  <div
                    className="p-4 rounded mb-2"
                    style={{
                      background: "linear-gradient(to right, #f8f9fa, #e9ecef)",
                      borderLeft: "4px solid #2660a5",
                    }}
                  >
                    <h5 className="mb-4 font-weight-bold">
                      Your Current Usage:
                    </h5>
                    {teamMemberCountLoading ? (
                      <div className="text-center py-2">
                        <SpinnerLoader />
                        <p className="mt-2 mb-0">
                          Loading team member count...
                        </p>
                      </div>
                    ) : (
                      <>
                        <Row className="mb-2">
                          <Col md={6}>
                            <div className="d-flex align-items-center mb-3">
                              <div
                                className="usage-label"
                                style={{ width: "50%" }}
                              >
                                <span style={{ fontSize: "16px" }}>
                                  Current Support Team:
                                </span>
                              </div>
                              <div>
                                <span
                                  className="badge p-2 ml-2"
                                  style={{
                                    fontSize: "16px",
                                    backgroundColor: "#e9ecef",
                                    color: "#333",
                                  }}
                                >
                                  {currentTeamMemberCount} members
                                </span>
                              </div>
                            </div>

                            <div className="d-flex align-items-center mb-3">
                              <div
                                className="usage-label"
                                style={{ width: "50%" }}
                              >
                                <span style={{ fontSize: "16px" }}>
                                  Free Allowance:
                                </span>
                              </div>
                              <div>
                                <span
                                  className="badge p-2 ml-2"
                                  style={{
                                    fontSize: "16px",
                                    backgroundColor: "#e9ecef",
                                    color: "#333",
                                  }}
                                >
                                  {freeAllowance} members
                                </span>
                              </div>
                            </div>
                          </Col>

                          <Col md={6}>
                            <div className="d-flex align-items-center mb-3">
                              <div
                                className="usage-label"
                                style={{ width: "50%" }}
                              >
                                <span style={{ fontSize: "16px" }}>
                                  Status:
                                </span>
                              </div>
                              <div>
                                {exceedsFreeAllowance ? (
                                  <span
                                    className="badge p-2 ml-2"
                                    style={{
                                      fontSize: "16px",
                                      backgroundColor: "#2660a5",
                                      color: "white",
                                    }}
                                  >
                                    Exceeds free tier
                                  </span>
                                ) : (
                                  <span
                                    className="badge p-2 ml-2"
                                    style={{
                                      fontSize: "16px",
                                      backgroundColor: "#28a745",
                                      color: "white",
                                    }}
                                  >
                                    Within free tier
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="d-flex align-items-center mb-3">
                              <div
                                className="usage-label"
                                style={{ width: "50%" }}
                              >
                                <span style={{ fontSize: "16px" }}>
                                  Monthly Cost:
                                </span>
                              </div>
                              <div>
                                {exceedsFreeAllowance ? (
                                  <span
                                    className="badge p-2 ml-2"
                                    style={{
                                      fontSize: "16px",
                                      backgroundColor: "#2660a5",
                                      color: "white",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    ${estimatedMonthlyCost.toFixed(2)}
                                  </span>
                                ) : (
                                  <span
                                    className="badge p-2 ml-2"
                                    style={{
                                      fontSize: "16px",
                                      backgroundColor: "#28a745",
                                      color: "white",
                                    }}
                                  >
                                    $0.00
                                  </span>
                                )}
                              </div>
                            </div>
                          </Col>
                        </Row>

                        {exceedsFreeAllowance && (
                          <div
                            className="mt-3 pt-3 border-top"
                            style={{ borderColor: "#dee2e6" }}
                          >
                            <div className="d-flex align-items-center">
                              <div style={{ fontSize: "16px" }}>
                                <strong>Billable Members:</strong>{" "}
                                {billableMembers}{" "}
                                <span style={{ color: "#6c757d" }}>
                                  ($
                                  {SUBSCRIPTION_PLAN.perUnitPrice.substring(
                                    1
                                  )}{" "}
                                  each)
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {exceedsFreeAllowance && (
                    <Alert
                      variant="info"
                      className="mb-4 border-left border-right-0 border-top-0 border-bottom-0 border-info"
                      style={{
                        borderLeftWidth: "4px",
                        backgroundColor: "rgba(38, 96, 165, 0.05)",
                        fontSize: "16px",
                      }}
                    >
                      <strong>Billing Summary:</strong> You have{" "}
                      {currentTeamMemberCount} team members, which exceeds the
                      free tier of 100.
                    </Alert>
                  )}

                  <h5 className="mb-4 font-weight-bold">Features Include:</h5>
                  <div className="features-list">
                    <Row>
                      {SUBSCRIPTION_PLAN.features.map((feature, index) => (
                        <Col md={6} key={index}>
                          <div className="d-flex align-items-start mb-3">
                            <div className="feature-icon mr-3 text-primary">
                              <i className="fa fa-check-circle"></i>
                            </div>
                            <div>{feature}</div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>

                  <div className="text-center mt-5 pt-3">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleSubscribe}
                      disabled={processing || teamMemberCountLoading}
                      className="px-5 py-3"
                      style={{
                        background:
                          "linear-gradient(135deg, #2660a5 0%, #4a8eda 100%)",
                        borderRadius: "2rem",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.12)",
                        border: "none",
                      }}
                    >
                      {processing ? (
                        <React.Fragment>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="mr-2"
                          />
                          Processing...
                        </React.Fragment>
                      ) : (
                        `Subscribe Now`
                      )}
                    </Button>
                  </div>
                </Card.Body>
                <Card.Footer
                  className="text-muted text-center py-4"
                  style={{
                    borderBottomLeftRadius: "1rem",
                    borderBottomRightRadius: "1rem",
                  }}
                >
                  <small>
                    By subscribing, you agree to our terms of service and will
                    be billed based on your monthly usage.
                  </small>
                </Card.Footer>
              </Card>

              <div className="text-center mt-4">
                <Button
                  variant="outline-primary"
                  onClick={() =>
                    history.push("/groupleader/groupleaderdashboard")
                  }
                  style={{ borderRadius: "2rem" }}
                >
                  <i className="fa fa-arrow-left mr-2"></i>
                  Return to Dashboard
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
}
